import React from 'react';
import FundraisingBar from '../FundraisingBar';
import { CampaignDetailHeader, EventTimeAndLocationDetails } from '@charitystack/shared';
import { getGlobalImageUrl } from '../config/env';
import ClampedAbout from './ClampedAbout';
import OverlaySupporters from './OverlaySupporters';
import { OVERLAY_MOBILE_DESCRIPTION_CLASS } from './overlayContent';

/**
 * CampaignLeftPanel — the overlay's layout below 1024 (CON-520, Paper 6PO-0 / 728-0), which
 * mirrors the hosted page's mobile scroll (Paper 588-0) without importing hosted-page.
 *
 * Header (title 24/30, by-line — the logo sits in the overlay's 56 header row instead —,
 * hero, ProgressStat) → supporter avatar row → event date / location tiles → About →
 * Location map. About shows a few lines, then Learn more opens the full description and the
 * FAQs in a sheet over the overlay (ClampedAbout). Sections 24 apart; inside a section the
 * SectionHeader sits 12 above its content.
 *
 * Props use the same flat shape as hosted-page pageData.
 */
function CampaignLeftPanel({
  // Header
  formName,
  organizedBy,
  merchantName,
  formID,
  formType,
  headerImage,
  imageVersion,
  logo,
  logoVersion,
  brandColors,
  previewMode = false,
  // Fundraising bar
  showFundraisingBar = false,
  fundraisingBarID,
  raisedAmount = 0,
  targetAmount = 0,
  trackingMetric,
  subscriptionCount,
  metricLabel,
  elementOffset,
  // Description
  fundraiserDetails,
  // FAQs — accept both casing conventions (hosted-page uses formFAQs, overlay data uses formFaqs)
  formFAQs,
  formFaqs,
  // Supporters
  enableDonorList = false,
  donorListID,
  donorListType = 'RECENT_DONORS',
  donorListData = null,
  showAllSupportersAnonymous = false,
  onGive,
  portalContainer,
  // Event details
  enableTimeAndLocation,
  eventStartDate,
  eventEndDate,
  eventStartTime,
  eventEndTime,
  eventLocation,
  timeZone,
}) {
  const imageBaseUrl = getGlobalImageUrl();
  const faqs = formFAQs || formFaqs || [];
  // Handoff map bug fix: a legacy event saved before the toggle existed has no
  // `enableTimeAndLocation` at all — missing means on.
  const showEventDetails = formType === 'EVENT' && enableTimeAndLocation !== false;

  return (
    <div className="flex flex-1 flex-col gap-[var(--cs-space-6)]">
      <div className="flex flex-col gap-[var(--cs-space-3)]">
        <CampaignDetailHeader
          formName={formName}
          organizedBy={organizedBy}
          merchantName={merchantName}
          formID={formID}
          formType={formType}
          imageBaseUrl={imageBaseUrl}
          headerImage={headerImage}
          imageVersion={imageVersion}
          logo={logo}
          logoVersion={logoVersion}
          bylineLogo="never"
          compact
          // CON-72: the widget's hero is sized by the HOST page's iframe, so `sizes` is
          // viewport-relative here — never the hosted rail's layout.
          imageSizes="100vw"
          brandColors={brandColors}
          previewMode={previewMode}
          // Events show no progress and no supporters (Paper 6S3-0).
          showFundraisingBar={showFundraisingBar && formType !== 'EVENT'}
          renderFundraisingBar={() => (
            <FundraisingBar
              fundraisingBarID={fundraisingBarID ? fundraisingBarID : previewMode ? 'NEW_FORM' : ''}
              brandColors={brandColors}
              amounts={{
                raisedAmount,
                targetAmount,
                trackingMetric,
                subscriptionCount,
                metricLabel,
                elementOffset,
              }}
              previewMode={previewMode}
            />
          )}
        />
        <OverlaySupporters
          formType={formType}
          enableDonorList={enableDonorList}
          donorListID={donorListID}
          donorListType={donorListType}
          donorListData={donorListData}
          showAllSupportersAnonymous={showAllSupportersAnonymous}
          brandColors={brandColors}
          previewMode={previewMode}
          onGive={onGive}
          portalContainer={portalContainer}
        />
      </div>

      {showEventDetails && (
        <EventTimeAndLocationDetails
          part="tiles"
          eventStartDate={eventStartDate}
          eventEndDate={eventEndDate}
          eventStartTime={eventStartTime}
          eventEndTime={eventEndTime}
          eventLocation={eventLocation}
          timeZone={timeZone}
        />
      )}

      {/* About: a few lines, then Learn more (full description + FAQs) or FAQs (ClampedAbout).
          The sheet covers the overlay's screen-height frame (OverlayElement's data-pane-sheet-scope). */}
      <ClampedAbout
        html={fundraiserDetails}
        descriptionClassName={OVERLAY_MOBILE_DESCRIPTION_CLASS}
        lineHeightVar="--cs-leading-24"
        title={formName}
        faqs={Array.isArray(faqs) && faqs.length > 0 ? faqs : null}
        elementID={formID}
      />

      {showEventDetails && <EventTimeAndLocationDetails part="map" eventLocation={eventLocation} />}
    </div>
  );
}

export default CampaignLeftPanel;
